/**
 * @file 积分消耗详情弹窗（全局单例，首页/工作台共用）
 */
import {useCallback} from 'react';
import PersonalPointsSummary from './PersonalPointsSummary';
import PointsDetail from './PointsDetail';
import styles from './index.module.less';
import {cx} from '@/utils/cx';
import Modal from '@/components/common/Modal';
import {useModalStore} from '@/store/useModalStore';
import type {PointsModalPayload} from '@/store/useModalStore';
import {TrackEvents} from '@/services/tracking/constant';
import {useTracking} from '@/services/tracking/context';
import type {LogExt} from '@/services/tracking';
import Button from '@/components/common/Button';
import {openMembershipModal, openPointsRechargeModal} from '@/commerce/modals/openModal';
import {MembershipModalSource} from '@/commerce/modals/MembershipModal/types';
import {PointsRechargeModalSource} from '@/commerce/modals/PointsRechargeModal/types';
import {useMemberExtShowLog, useMemberLogExt} from '@/hooks/memberLogExt';
import {useIsProducer} from '@/hooks/useIsProducer';
import {useContextMemberInfo} from '@/commerce/benefit';
import {MemberLevelValue} from '@/services/commerce/type';

interface PointsModalView {
    /** 弹窗标题 */
    title: string;
    /** 埋点 from */
    from: string;
    /** 是否用户维度，区别于项目维度 */
    isPersonal: boolean;
    /** 注入弹窗最外层的样式 */
    modalClass?: string;
    /** 埋点 ext */
    trackExt: LogExt;
}

/** payload union 的唯一解构处，两种维度的差异集中在此声明 */
const describePayload = (payload: PointsModalPayload): PointsModalView =>
    payload.type === 'personal'
        ? {
              title: '积分详情',
              from: 'home',
              isPersonal: true,
              // eslint-disable-next-line camelcase
              trackExt: {project_id: '', projectName: ''},
          }
        : {
              title: '积分明细',
              from: 'workspace',
              isPersonal: false,
              modalClass: styles.projectModal,
              // eslint-disable-next-line camelcase
              trackExt: {project_id: payload.projectId, projectName: payload.projectName},
          };

const Agreement: React.FC<{from: string}> = ({from}) => {
    const closePointsModal = useModalStore((s) => s.closePointsModal);
    const isProducer = useIsProducer();
    const {memberInfo, isLoading} = useContextMemberInfo();
    const isMember = !!memberInfo && memberInfo.memberLevel !== MemberLevelValue.FREE;
    const {sendClickLog} = useTracking();
    const memberLogExt = useMemberLogExt();

    const handleClick = useCallback(() => {
        sendClickLog({
            page: TrackEvents.PAGE_HOME,
            from,
            value: isMember ? TrackEvents.CREDIT_WINDOW_CREDIT : TrackEvents.CREDIT_WINDOW_MEMBER,
            ext: isMember ? memberLogExt : undefined,
        });

        if (isMember) {
            openPointsRechargeModal(
                PointsRechargeModalSource.POINTS_CONSUMPTION_DETAILS_PAGE_POPUP_RECHARGE_POINTS_BTN,
            );
        } else {
            openMembershipModal(
                MembershipModalSource.POINTS_CONSUMPTION_DETAILS_PAGE_POPUP_MEMBERSHIP_BTN,
            );
        }
        closePointsModal();
    }, [isMember, closePointsModal, from, sendClickLog, memberLogExt]);

    return (
        <div className={styles.agreement}>
            <span className={styles.text}>
                图片与视频生成由于生成数量、分辨率、时长等参数不同，费用存在差异
                <a href="/membershipAgreement" target="_blank" rel="noopener noreferrer">
                    会员与积分协议
                </a>
            </span>
            {isProducer && !isLoading && (
                <Button type="primary" className={styles.btn} onClick={handleClick}>
                    {isMember ? '充值积分' : '订阅会员'}
                </Button>
            )}
        </div>
    );
};

const PointsModal: React.FC = () => {
    const payload = useModalStore((s) => s.pointsModalPayload);
    const closePointsModal = useModalStore((s) => s.closePointsModal);
    const view = payload && describePayload(payload);

    useMemberExtShowLog(
        view && {
            page: TrackEvents.PAGE_HOME,
            from: view.from,
            value: TrackEvents.CREDIT_WINDOW,
            ext: view.trackExt,
        },
    );

    return (
        <Modal
            open={!!payload}
            onCancel={closePointsModal}
            destroyOnHidden
            size="lg"
            className={cx(styles.modal, view?.modalClass)}
            title={view?.title}
            footer={null}
        >
            {payload && view && (
                <>
                    {view.isPersonal && <PersonalPointsSummary />}
                    <PointsDetail {...payload} />
                    <Agreement from={view.from} />
                </>
            )}
        </Modal>
    );
};

export default PointsModal;
